import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator, { isStaff } from "../utilities/checkPrivileges";
import schoolClassService from "../services/schoolClassService";
import directoryUserService from "../services/directoryUserService";
import locationService from "../services/locationService";
import deviceGroupService from "../services/deviceGroupService";
import { useQuery } from "@apollo/client";
import { GET_CONSOLE_USERS } from "../queries/userQueries";
import Spinner from "../components/Spinner";
import AppPickerModal, { VppAppInstallModal } from "../components/classroom/AppPickerModal";
import SafariControlModal from "../components/classroom/SafariControlModal";
import ClassroomLayoutModal from "../components/classroom/ClassroomLayoutModal";
import { useProfileEditorAppCatalog } from "../hooks/useProfileEditorAppCatalog";
import AppIcon from "../components/AppIcon";
import { deviceLastSeenLabel } from "../utilities/deviceLastSeen";
import "./Classroom.css";

const emptyTemplate = {
  name: "",
  durationMinutes: 45,
  applyEducationProfile: true,
  cameraAllowed: null,
  safariAllowed: null,
  bluetoothEnabled: null,
  blockedAppBundleIds: "",
  notes: "",
  safariFilterMode: "off",
  safariDenyUrls: [],
  safariAllowUrls: [],
  safariBookmarks: [],
  homeScreenLayout: null,
  webClips: [],
  allowListedAppBundleIds: null,
};

const emptyForm = {
  name: "",
  location: "",
  deviceGroup: "",
  teachers: [],
  students: [],
  consoleTeachers: [],
};

function idOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id ? String(value._id) : "";
}

function idsOf(list) {
  return (list || []).map(idOf).filter(Boolean);
}

function formatEndsAt(endsAt) {
  if (!endsAt) return "";
  try {
    return new Date(endsAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** null = Standardprofil; true/false = Unterrichts-Override. */
function cycleOverride(value) {
  if (value === null || value === undefined) return true;
  if (value === true) return false;
  return null;
}

function overrideLabel(value) {
  if (value === true) return "an";
  if (value === false) return "aus";
  return "Standard";
}

function overrideClass(value) {
  if (value === true) return "is-on";
  if (value === false) return "is-off";
  return "is-default";
}

export default function Classroom({ fullscreen = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFullscreen = fullscreen || searchParams.get("fullscreen") === "1";
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();
  const staff = isStaff();
  const [tab, setTab] = useState("control");
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lessons, setLessons] = useState({});
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [devices, setDevices] = useState([]);
  const [selectedUdids, setSelectedUdids] = useState([]);
  const [blacklistIds, setBlacklistIds] = useState([]);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [showSafariModal, setShowSafariModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [showTemplateLayoutModal, setShowTemplateLayoutModal] = useState(false);
  const [showTemplateSafariModal, setShowTemplateSafariModal] = useState(false);
  const [installedApps, setInstalledApps] = useState([]);
  const { appsById } = useProfileEditorAppCatalog(blacklistIds);
  const { data: consoleData } = useQuery(GET_CONSOLE_USERS, { skip: !admin });
  const consoleTeachers = (consoleData?.consoleusers || []).filter(
    (row) => row.userType === "consoleTeacher" || row.userType === "consoleAdministrator"
  );

  useEffect(() => {
    if (!user) {
      const next = isFullscreen ? "/teacher" : "/classroom";
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
  }, [user, navigate, isFullscreen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await schoolClassService.getClassroom();
      setClasses(data.classes || []);
      setTemplates(data.templates || []);
      setLessons(data.lessons || {});
      setSelectedClassId((prev) => prev || (data.classes?.[0]?._id ? String(data.classes[0]._id) : ""));
      if (admin) {
        const [dir, locs, grps] = await Promise.all([
          directoryUserService.listDirectoryUsers(),
          locationService.getLocations(),
          deviceGroupService.getDeviceGroups(),
        ]);
        setUsers(dir || []);
        setLocations(locs || []);
        setGroups(grps || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unterrichtsansicht konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [admin]);

  const loadRoom = useCallback(async (classId) => {
    if (!classId) {
      setDevices([]);
      setInstalledApps([]);
      return;
    }
    try {
      const [deviceData, appsData] = await Promise.all([
        schoolClassService.listClassDevices(classId),
        schoolClassService.listInstalledApps(classId).catch(() => ({ apps: [] })),
      ]);
      setDevices(deviceData.devices || []);
      setInstalledApps(appsData.apps || []);
      if (Array.isArray(deviceData.lesson?.controls?.blockedAppBundleIds)) {
        setBlacklistIds([...(deviceData.lesson.controls.blockedAppBundleIds || [])]);
      } else {
        setBlacklistIds([]);
      }
      if (deviceData.lesson) {
        setLessons((prev) => ({ ...prev, [String(classId)]: deviceData.lesson }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Klassengeräte konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => {
    if (user && staff) load();
  }, [user, staff, load]);

  useEffect(() => {
    if (selectedClassId) {
      setSelectedUdids([]);
      loadRoom(selectedClassId);
    }
  }, [selectedClassId, loadRoom]);

  const selectedClass = useMemo(
    () => classes.find((row) => String(row._id) === String(selectedClassId)) || null,
    [classes, selectedClassId]
  );
  const activeLesson = lessons[selectedClassId] || null;
  const controls = activeLesson?.controls || {
    cameraAllowed: null,
    safariAllowed: null,
    bluetoothEnabled: null,
    blockedAppBundleIds: null,
    allowListedAppBundleIds: null,
    webClips: [],
  };

  const scopeUdids = selectedUdids.length ? selectedUdids : null;
  const scopeLabel = selectedUdids.length
    ? `${selectedUdids.length} Gerät(e)`
    : "ganze Klasse";

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
      if (selectedClassId) await loadRoom(selectedClassId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const toggleDevice = (udid) => {
    setSelectedUdids((prev) => (
      prev.includes(udid) ? prev.filter((id) => id !== udid) : [...prev, udid]
    ));
  };

  const applyControl = async (patch, label) => {
    if (!selectedClassId) return;
    await run(`ctrl-${label}`, () => schoolClassService.applyControls(selectedClassId, {
      controls: patch,
      udids: scopeUdids,
    }), `${label} → ${scopeLabel}`);
  };

  const classPayload = () => ({
    ...form,
    location: form.location || null,
    deviceGroup: form.deviceGroup || null,
  });

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name || "",
      location: idOf(row.location),
      deviceGroup: idOf(row.deviceGroup),
      teachers: idsOf(row.teachers),
      students: idsOf(row.students),
      consoleTeachers: idsOf(row.consoleTeachers),
    });
    setTab("admin");
  };

  const saveClass = async (event) => {
    event.preventDefault();
    setBusy("save");
    try {
      if (editingId) {
        await schoolClassService.updateClass(editingId, classPayload());
        toast.success("Klasse aktualisiert.");
      } else {
        await schoolClassService.createClass(classPayload());
        toast.success("Klasse angelegt.");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Speichern fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const removeClass = async (row) => {
    if (!window.confirm(`Klasse „${row.name}“ wirklich löschen?`)) return;
    await run(`del-${row._id}`, () => schoolClassService.deleteClass(row._id), "Klasse gelöscht.");
    if (editingId === row._id) resetForm();
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setBusy("tpl");
    try {
      const blocked = String(templateForm.blockedAppBundleIds || "")
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        name: templateForm.name.trim(),
        durationMinutes: Number(templateForm.durationMinutes) || 45,
        applyEducationProfile: Boolean(templateForm.applyEducationProfile),
        cameraAllowed: templateForm.cameraAllowed,
        safariAllowed: templateForm.safariAllowed,
        bluetoothEnabled: templateForm.bluetoothEnabled,
        blockedAppBundleIds: blocked.length ? blocked : null,
        allowListedAppBundleIds: templateForm.allowListedAppBundleIds || null,
        safariFilterMode: templateForm.safariFilterMode || "off",
        safariDenyUrls: templateForm.safariDenyUrls || [],
        safariAllowUrls: templateForm.safariAllowUrls || [],
        safariBookmarks: templateForm.safariBookmarks || [],
        homeScreenLayout: templateForm.homeScreenLayout || null,
        webClips: templateForm.webClips || [],
        notes: templateForm.notes || "",
      };
      if (!payload.name) {
        toast.error("Name der Vorlage ist erforderlich.");
        return;
      }
      if (editingTemplateId) {
        await schoolClassService.updateLessonTemplate(editingTemplateId, payload);
        toast.success("Vorlage aktualisiert.");
      } else {
        await schoolClassService.createLessonTemplate(payload);
        toast.success("Vorlage angelegt.");
      }
      setEditingTemplateId(null);
      setTemplateForm(emptyTemplate);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Vorlage speichern fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const startEditTemplate = (tpl) => {
    setEditingTemplateId(tpl._id);
    setTemplateForm({
      name: tpl.name || "",
      durationMinutes: tpl.durationMinutes || 45,
      applyEducationProfile: tpl.applyEducationProfile !== false,
      cameraAllowed: tpl.cameraAllowed === true || tpl.cameraAllowed === false ? tpl.cameraAllowed : null,
      safariAllowed: tpl.safariAllowed === true || tpl.safariAllowed === false ? tpl.safariAllowed : null,
      bluetoothEnabled: tpl.bluetoothEnabled === true || tpl.bluetoothEnabled === false ? tpl.bluetoothEnabled : null,
      blockedAppBundleIds: Array.isArray(tpl.blockedAppBundleIds) ? tpl.blockedAppBundleIds.join("\n") : "",
      allowListedAppBundleIds: tpl.allowListedAppBundleIds || null,
      safariFilterMode: tpl.safariFilterMode || "off",
      safariDenyUrls: tpl.safariDenyUrls || [],
      safariAllowUrls: tpl.safariAllowUrls || [],
      safariBookmarks: tpl.safariBookmarks || [],
      homeScreenLayout: tpl.homeScreenLayout || null,
      webClips: tpl.webClips || [],
      notes: tpl.notes || "",
    });
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Vorlage „${tpl.name}“ wirklich löschen?`)) return;
    await run(`tdel-${tpl._id}`, () => schoolClassService.deleteLessonTemplate(tpl._id), "Vorlage gelöscht.");
    if (editingTemplateId === tpl._id) {
      setEditingTemplateId(null);
      setTemplateForm(emptyTemplate);
    }
  };

  if (!staff) {
    return <p className="mt-4">Die Lehreransicht ist Administratoren und Lehrern vorbehalten.</p>;
  }
  if (loading) return <Spinner />;

  return (
    <div className={`teacher-room ${isFullscreen ? "teacher-room--fullscreen" : ""}`}>
      <header className="teacher-room__hero">
        <div>
          <p className="teacher-room__eyebrow">Xavier Teacher</p>
          <h2 className="teacher-room__title">Klassenraum</h2>
          <p className="teacher-room__lead">
            Unterricht live steuern — für die ganze Klasse oder einzelne Geräte.
            {isFullscreen ? " Webclip-Modus." : ""}
          </p>
          {!isFullscreen ? (
            <p className="teacher-room__webclip-hint">
              Vollbild/Webclip: <code>/teacher</code>
            </p>
          ) : null}
        </div>
        <div className="teacher-room__tabs">
          <button
            type="button"
            className={tab === "control" ? "is-active" : ""}
            onClick={() => setTab("control")}
          >
            Steuerung
          </button>
          {admin ? (
            <button
              type="button"
              className={tab === "admin" ? "is-active" : ""}
              onClick={() => setTab("admin")}
            >
              Verwaltung
            </button>
          ) : null}
        </div>
      </header>

      {tab === "control" ? (
        <div className="teacher-room__layout">
          <aside className="teacher-room__classes">
            <h3>Klassen</h3>
            {!classes.length ? (
              <p className="text-muted small">Noch keine Klassen zugewiesen.</p>
            ) : null}
            {classes.map((row) => {
              const active = Boolean(lessons[String(row._id)]);
              return (
                <button
                  key={row._id}
                  type="button"
                  className={`teacher-room__class ${String(row._id) === selectedClassId ? "is-selected" : ""}`}
                  onClick={() => setSelectedClassId(String(row._id))}
                >
                  <span className="teacher-room__class-name">{row.name}</span>
                  <span className="teacher-room__class-meta">
                    {(row.students || []).length} Schüler
                    {active ? " · aktiv" : ""}
                  </span>
                </button>
              );
            })}
          </aside>

          <section className="teacher-room__main">
            {!selectedClass ? (
              <p className="text-muted">Bitte eine Klasse wählen.</p>
            ) : (
              <>
                <div className="teacher-room__toolbar">
                  <div>
                    <h3>{selectedClass.name}</h3>
                    <p className="teacher-room__scope">
                      Ziel: <strong>{scopeLabel}</strong>
                      {activeLesson?.endsAt ? ` · bis ${formatEndsAt(activeLesson.endsAt)}` : ""}
                    </p>
                  </div>
                  <div className="teacher-room__toolbar-actions">
                    {!activeLesson ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={Boolean(busy)}
                          onClick={() => run("start", () => schoolClassService.startLesson(selectedClassId), "Unterricht gestartet.")}
                        >
                          Stunde starten
                        </button>
                        {templates.map((tpl) => (
                          <button
                            key={tpl._id}
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={Boolean(busy)}
                            onClick={() => run(
                              `preset-${tpl._id}`,
                              () => schoolClassService.startLesson(selectedClassId, { templateId: tpl._id }),
                              `Preset „${tpl.name}“ gestartet.`
                            )}
                          >
                            {tpl.name}
                          </button>
                        ))}
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        disabled={Boolean(busy)}
                        onClick={() => run("end", () => schoolClassService.endLesson(selectedClassId), "Unterricht beendet, Steuerung entfernt.")}
                      >
                        Stunde beenden
                      </button>
                    )}
                  </div>
                </div>

                <div className="teacher-room__controls">
                  <button
                    type="button"
                    className={`teacher-room__toggle ${overrideClass(controls.cameraAllowed)}`}
                    disabled={Boolean(busy)}
                    title="Standard → an → aus → Standard"
                    onClick={() => applyControl({ cameraAllowed: cycleOverride(controls.cameraAllowed) }, "Kamera")}
                  >
                    <span>Kamera</span>
                    <strong>{overrideLabel(controls.cameraAllowed)}</strong>
                  </button>
                  <button
                    type="button"
                    className={`teacher-room__toggle ${overrideClass(controls.safariAllowed)}`}
                    disabled={Boolean(busy)}
                    title="Standard → an → aus → Standard"
                    onClick={() => applyControl({ safariAllowed: cycleOverride(controls.safariAllowed) }, "Safari")}
                  >
                    <span>Safari</span>
                    <strong>{overrideLabel(controls.safariAllowed)}</strong>
                  </button>
                  <button
                    type="button"
                    className={`teacher-room__toggle ${overrideClass(controls.bluetoothEnabled)}`}
                    disabled={Boolean(busy)}
                    title="Standard → an → aus → Standard"
                    onClick={() => applyControl({ bluetoothEnabled: cycleOverride(controls.bluetoothEnabled) }, "Bluetooth")}
                  >
                    <span>Bluetooth</span>
                    <strong>{overrideLabel(controls.bluetoothEnabled)}</strong>
                  </button>
                  <button
                    type="button"
                    className="teacher-room__action"
                    disabled={Boolean(busy)}
                    onClick={() => setShowSafariModal(true)}
                  >
                    Safari Filter
                  </button>
                  <button
                    type="button"
                    className="teacher-room__action"
                    disabled={Boolean(busy)}
                    onClick={async () => {
                      if (selectedClassId) {
                        try {
                          const appsData = await schoolClassService.listInstalledApps(selectedClassId);
                          setInstalledApps(appsData.apps || []);
                        } catch {
                          /* ignore */
                        }
                      }
                      setShowLayoutModal(true);
                    }}
                  >
                    Layout
                  </button>
                  <button
                    type="button"
                    className="teacher-room__action"
                    disabled={Boolean(busy)}
                    onClick={() => run("inv", async () => {
                      await schoolClassService.refreshInventory(selectedClassId, { udids: scopeUdids });
                      if (selectedClassId) {
                        const appsData = await schoolClassService.listInstalledApps(selectedClassId).catch(() => ({ apps: [] }));
                        setInstalledApps(appsData.apps || []);
                      }
                    }, `Inventar aktualisiert (${scopeLabel}).`)}
                  >
                    Inventory
                  </button>
                  <button
                    type="button"
                    className="teacher-room__action"
                    disabled={Boolean(busy)}
                    onClick={() => {
                      if (!window.confirm(`Geräte sperren (${scopeLabel})?`)) return;
                      run("lock", () => schoolClassService.lockDevices(selectedClassId, { udids: scopeUdids }), `Sperre gesendet (${scopeLabel}).`);
                    }}
                  >
                    Sperren
                  </button>
                  <button
                    type="button"
                    className="teacher-room__action"
                    disabled={Boolean(busy)}
                    onClick={() => run("edu", () => schoolClassService.pushEducation(selectedClassId), "Classroom-Profil gesendet.")}
                  >
                    Classroom-Profil
                  </button>
                </div>

                <div className="teacher-room__panels">
                  <div className="teacher-room__panel">
                    <h4>App-Blacklist</h4>
                    <div className="teacher-modal__chips mb-2">
                      {blacklistIds.length ? blacklistIds.map((id) => (
                        <span key={id} className="teacher-modal__chip teacher-modal__chip--static">
                          <AppIcon src={appsById.get(id)?.artworkUrl} name={appsById.get(id)?.name || id} size={22} />
                          <span>{appsById.get(id)?.name || id}</span>
                        </span>
                      )) : (
                        <span className="small text-muted">
                          {Array.isArray(controls.allowListedAppBundleIds) && controls.allowListedAppBundleIds.length
                            ? "Allowlist aktiv (über Layout) — Blacklist nicht gesteuert."
                            : "Keine Blacklist-Steuerung (Standardprofil gilt)."}
                        </span>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={async () => {
                          if (selectedClassId) {
                            try {
                              const appsData = await schoolClassService.listInstalledApps(selectedClassId);
                              setInstalledApps(appsData.apps || []);
                            } catch {
                              /* ignore */
                            }
                          }
                          setShowBlacklistModal(true);
                        }}
                      >
                        Apps wählen…
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={Boolean(busy)}
                        onClick={() => applyControl({
                          blockedAppBundleIds: blacklistIds.length ? blacklistIds : null,
                          allowListedAppBundleIds: null,
                        }, "Blacklist")}
                      >
                        Blacklist anwenden
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={Boolean(busy)}
                        onClick={() => {
                          setBlacklistIds([]);
                          applyControl({ blockedAppBundleIds: null }, "Blacklist");
                        }}
                      >
                        Blacklist zurücknehmen
                      </button>
                    </div>
                  </div>

                  {admin ? (
                    <div className="teacher-room__panel">
                      <h4>Apps installieren</h4>
                      <p className="small text-muted mb-2">Aus dem VPP-/App-Katalog auf Schülergeräte pushen.</p>
                      <button type="button" className="btn btn-sm btn-primary" disabled={Boolean(busy)} onClick={() => setShowInstallModal(true)}>
                        Katalog öffnen…
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="teacher-room__devices-head">
                  <h4>
                    Geräte ({devices.length})
                    <span className="teacher-room__devices-hint">
                      {" "}· Lehrergeräte werden nicht eingeschränkt
                    </span>
                  </h4>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedUdids(devices.filter((d) => !d.isTeacherDevice).map((d) => d.udid))}
                    >
                      Alle Schüler
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedUdids([])}>
                      Auswahl leeren
                    </button>
                  </div>
                </div>
                <div className="teacher-room__grid">
                  {devices.map((device) => {
                    const selected = selectedUdids.includes(device.udid);
                    const teacher = Boolean(device.isTeacherDevice);
                    return (
                      <button
                        key={device.udid}
                        type="button"
                        className={`teacher-room__device ${selected ? "is-selected" : ""} ${teacher ? "is-teacher" : ""}`}
                        onClick={() => {
                          if (teacher) return;
                          toggleDevice(device.udid);
                        }}
                        title={teacher ? "Lehrergerät — keine Einschränkungen" : undefined}
                      >
                        <span className="teacher-room__device-name">{device.deviceName}</span>
                        <span className="teacher-room__device-user">
                          {teacher ? "Lehrer" : (device.assignedDirectoryUser?.name || "kein Benutzer")}
                        </span>
                        <span className="teacher-room__device-meta">
                          {device.productName || "iPad/iPhone"}
                          {device.osVersion ? ` · iOS ${device.osVersion}` : ""}
                        </span>
                        <span className="teacher-room__device-checkin">
                          Check-in: {deviceLastSeenLabel(device)}
                        </span>
                      </button>
                    );
                  })}
                  {!devices.length ? (
                    <p className="text-muted">Keine Geräte. Benutzer zuweisen oder Gerätegruppe an der Klasse setzen.</p>
                  ) : null}
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}

      {tab === "admin" && admin ? (
        <div className="mt-3">
          {classes.map((row) => (
            <div className="card p-3 mb-3" key={row._id}>
              <div className="d-flex justify-content-between flex-wrap gap-2">
                <div>
                  <h3 className="h5 mb-1">{row.name}</h3>
                  <div className="small text-muted">
                    {row.location?.name || "kein Standort"}
                    {row.deviceGroup?.name ? ` · Gruppe ${row.deviceGroup.name}` : ""}
                    {" · "}
                    {(row.teachers || []).length} Lehrkräfte, {(row.students || []).length} Schüler
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={Boolean(busy)} onClick={() => startEdit(row)}>
                    Bearbeiten
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy)} onClick={() => removeClass(row)}>
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="card p-3 mb-3" id="class-form">
            <h3 className="h5">{editingId ? "Klasse bearbeiten" : "Klasse anlegen"}</h3>
            <form onSubmit={saveClass} className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Name</label>
                <input className="form-control" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Standort</label>
                <select className="form-select" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
                  <option value="">—</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Gerätegruppe</label>
                <select className="form-select" value={form.deviceGroup} onChange={(e) => setForm({ ...form, deviceGroup: e.target.value })}>
                  <option value="">—</option>
                  {groups.map((grp) => (
                    <option key={grp._id} value={grp._id}>{grp.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Lehrer</label>
                <select multiple className="form-select" value={form.teachers} onChange={(e) => setForm({ ...form, teachers: [...e.target.selectedOptions].map((o) => o.value) })}>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Schüler</label>
                <select multiple className="form-select" value={form.students} onChange={(e) => setForm({ ...form, students: [...e.target.selectedOptions].map((o) => o.value) })}>
                  {users.filter((u) => u.role === "student").map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Xavier-Lehrerzugriff (Console)</label>
                <select multiple className="form-select" value={form.consoleTeachers} onChange={(e) => setForm({ ...form, consoleTeachers: [...e.target.selectedOptions].map((o) => o.value) })}>
                  {consoleTeachers.map((row) => (
                    <option key={row._id} value={row._id}>
                      {row.name} ({row.userType === "consoleTeacher" ? "Lehrer" : "Admin"})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 d-flex gap-2">
                <button className="btn btn-primary" type="submit" disabled={Boolean(busy)}>
                  {editingId ? "Speichern" : "Anlegen"}
                </button>
                {editingId ? (
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={Boolean(busy)}>Abbrechen</button>
                ) : null}
              </div>
            </form>
          </div>

          <div className="card p-3">
            <h3 className="h5">{editingTemplateId ? "Unterrichts-Preset bearbeiten" : "Unterrichts-Preset"}</h3>
            <p className="small text-muted mb-2">
              Presets können Layout, Safari-Filter und Overrides vorbereiten. „Standard“ lässt das Geräteprofil unverändert.
            </p>
            <form onSubmit={saveTemplate} className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Name</label>
                <input className="form-control" required value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Dauer (Min.)</label>
                <input type="number" min="1" className="form-control" value={templateForm.durationMinutes} onChange={(e) => setTemplateForm({ ...templateForm, durationMinutes: e.target.value })} />
              </div>
              <div className="col-md-6 d-flex flex-wrap align-items-end gap-3">
                <div className="form-check">
                  <input
                    id="tpl-edu"
                    type="checkbox"
                    className="form-check-input"
                    checked={Boolean(templateForm.applyEducationProfile)}
                    onChange={(e) => setTemplateForm({ ...templateForm, applyEducationProfile: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="tpl-edu">Classroom-Profil</label>
                </div>
              </div>
              {[
                ["cameraAllowed", "Kamera"],
                ["safariAllowed", "Safari"],
                ["bluetoothEnabled", "Bluetooth"],
              ].map(([key, label]) => (
                <div className="col-md-4" key={key}>
                  <label className="form-label">{label}</label>
                  <select
                    className="form-select"
                    value={templateForm[key] === true ? "on" : templateForm[key] === false ? "off" : "std"}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTemplateForm({
                        ...templateForm,
                        [key]: v === "on" ? true : v === "off" ? false : null,
                      });
                    }}
                  >
                    <option value="std">Standard (Profil)</option>
                    <option value="on">An (Unterricht)</option>
                    <option value="off">Aus (Unterricht)</option>
                  </select>
                </div>
              ))}
              <div className="col-12">
                <label className="form-label">Blacklist (Bundle-IDs, eine pro Zeile)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={templateForm.blockedAppBundleIds}
                  onChange={(e) => setTemplateForm({ ...templateForm, blockedAppBundleIds: e.target.value })}
                />
              </div>
              <div className="col-12 d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowTemplateSafariModal(true)}
                >
                  Safari-Filter{templateForm.safariFilterMode && templateForm.safariFilterMode !== "off" ? " · gesetzt" : ""}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowTemplateLayoutModal(true)}
                >
                  Layout{templateForm.homeScreenLayout?.Pages ? " · gesetzt" : ""}
                </button>
              </div>
              <div className="col-12">
                <label className="form-label">Notizen</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={templateForm.notes}
                  onChange={(e) => setTemplateForm({ ...templateForm, notes: e.target.value })}
                />
              </div>
              <div className="col-12 d-flex gap-2">
                <button className="btn btn-primary" type="submit" disabled={Boolean(busy)}>
                  {editingTemplateId ? "Preset speichern" : "Preset anlegen"}
                </button>
                {editingTemplateId ? (
                  <button type="button" className="btn btn-outline-secondary" disabled={Boolean(busy)} onClick={() => { setEditingTemplateId(null); setTemplateForm(emptyTemplate); }}>
                    Abbrechen
                  </button>
                ) : null}
              </div>
            </form>
            {templates.length ? (
              <ul className="mt-3 mb-0 list-unstyled">
                {templates.map((tpl) => (
                  <li key={tpl._id} className="d-flex flex-wrap align-items-center gap-2 py-1">
                    <span>{tpl.name} · {tpl.durationMinutes} Min.</span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={Boolean(busy)} onClick={() => startEditTemplate(tpl)}>Bearbeiten</button>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy)} onClick={() => removeTemplate(tpl)}>Löschen</button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      <AppPickerModal
        open={showBlacklistModal}
        title="App-Blacklist"
        value={blacklistIds}
        installedApps={installedApps}
        emptyHint="Noch keine Apps inventarisiert. Bitte zuerst „Inventory“ für die Schülergeräte ausführen."
        onChange={setBlacklistIds}
        onClose={() => setShowBlacklistModal(false)}
        confirmLabel="Auswahl übernehmen"
      />
      <ClassroomLayoutModal
        open={showLayoutModal}
        value={controls.homeScreenLayout}
        webClips={controls.webClips || []}
        installedApps={installedApps}
        onClose={() => setShowLayoutModal(false)}
        onApply={(payload) => {
          if (payload === null) {
            applyControl({ homeScreenLayout: null, allowListedAppBundleIds: null, webClips: [] }, "Layout");
            return;
          }
          applyControl(payload, "Layout");
        }}
      />
      <SafariControlModal
        open={showSafariModal}
        value={{
          filterMode: controls.safariFilterMode || "off",
          denyUrls: controls.safariDenyUrls || [],
          allowUrls: controls.safariAllowUrls || [],
          bookmarks: controls.safariBookmarks || [],
        }}
        onClose={() => setShowSafariModal(false)}
        onApply={(safari) => applyControl({
          safariFilterMode: safari.filterMode,
          safariDenyUrls: safari.denyUrls,
          safariAllowUrls: safari.allowUrls,
          safariBookmarks: safari.bookmarks,
        }, "Safari")}
      />
      <ClassroomLayoutModal
        open={showTemplateLayoutModal}
        value={templateForm.homeScreenLayout}
        webClips={templateForm.webClips || []}
        installedApps={installedApps}
        onClose={() => setShowTemplateLayoutModal(false)}
        onApply={(payload) => {
          if (payload === null) {
            setTemplateForm({
              ...templateForm,
              homeScreenLayout: null,
              allowListedAppBundleIds: null,
              webClips: [],
            });
            return;
          }
          setTemplateForm({
            ...templateForm,
            homeScreenLayout: payload.homeScreenLayout,
            allowListedAppBundleIds: payload.allowListedAppBundleIds,
            webClips: payload.webClips || [],
            blockedAppBundleIds: "",
          });
        }}
      />
      <SafariControlModal
        open={showTemplateSafariModal}
        value={{
          filterMode: templateForm.safariFilterMode || "off",
          denyUrls: templateForm.safariDenyUrls || [],
          allowUrls: templateForm.safariAllowUrls || [],
          bookmarks: templateForm.safariBookmarks || [],
        }}
        onClose={() => setShowTemplateSafariModal(false)}
        onApply={(safari) => setTemplateForm({
          ...templateForm,
          safariFilterMode: safari.filterMode,
          safariDenyUrls: safari.denyUrls,
          safariAllowUrls: safari.allowUrls,
          safariBookmarks: safari.bookmarks,
        })}
      />
      {admin ? (
        <VppAppInstallModal
          open={showInstallModal}
          onClose={() => setShowInstallModal(false)}
          onConfirm={(adamIds) => run(
            "install",
            () => schoolClassService.installApps(selectedClassId, { adamIds, udids: scopeUdids }),
            `App-Installation gestartet (${scopeLabel}).`,
          )}
        />
      ) : null}
    </div>
  );
}
